import { Row } from 'app/design/primitives/box';
import { Input } from 'app/design/primitives/input';
import { Modal } from 'app/design/primitives/modal';
import type { useScopedTranslation } from 'app/hooks/i18n';
import { Button } from 'app/shopify';
import type React from 'react';
import Toast from 'react-native-toast-message';
import type { useListingBulkActions } from '../my-listings';
import { DuplicateListingsModal } from './duplicate-listings-modal';
import { ListingAlertModal } from './listing-alert-modal';

export function ListingBulkEditDialogs({
  actions,
  selectedRowsState,
  t,
}: {
  actions: ReturnType<typeof useListingBulkActions>;
  selectedRowsState: [string[], React.Dispatch<React.SetStateAction<string[]>>];
  t: ReturnType<typeof useScopedTranslation>['t'];
}) {
  const {
    showDuplicateModal,
    setShowDuplicateModal,
    showPriceModal,
    setShowPriceModal,
    duplicateCount,
    setDuplicateCount,
    price,
    setPrice,
    isDuplicatingListings,
    duplicateListings,
    updateListingsPrice,
    finishBulkEdit,
    alert,
    hideAlert,
  } = actions;
  return (
    <>
      <DuplicateListingsModal
        count={duplicateCount}
        isPending={isDuplicatingListings}
        onClose={() => setShowDuplicateModal(false)}
        open={showDuplicateModal}
        setCount={setDuplicateCount}
        onConfirm={() =>
          duplicateListings(
            { listingIds: selectedRowsState[0], count: duplicateCount },
            {
              onSuccess: () => {
                setShowDuplicateModal(false);
                finishBulkEdit();
                Toast.show({
                  type: 'success',
                  position: 'bottom',
                  bottomOffset: 100,
                  text1: t('duplicateToast.title'),
                  text2: t('duplicateToast.text'),
                  visibilityTime: 5000,
                });
              },
            },
          )
        }
      />

      <Modal onClose={() => setShowPriceModal(false)} open={showPriceModal} transparent>
        <Modal.Card gap="spacing.general.x6">
          <Modal.Title allowClose onClose={() => setShowPriceModal(false)}>
            {t('priceModal.title', { count: selectedRowsState[0].length })}
          </Modal.Title>
          <Input
            label={t('priceModal.label')}
            markAsRequired
            onChangeText={setPrice}
            placeholder={t('priceModal.placeholder')}
            value={price}
          />
          <Row gap="spacing.general.x4" justifyContent="flex-end">
            <Button label={t('cancelButton')} onPress={() => setShowPriceModal(false)} variant="secondaryNeutral" />
            <Button
              disabled={!price.trim()}
              label={t('priceModal.save')}
              onPress={() =>
                updateListingsPrice(
                  { listingIds: selectedRowsState[0], price: price.trim() },
                  {
                    onSuccess: () => {
                      setShowPriceModal(false);
                      finishBulkEdit();
                      Toast.show({
                        type: 'success',
                        position: 'bottom',
                        bottomOffset: 100,
                        text1: t('priceUpdatedToast.title'),
                        text2: t('priceUpdatedToast.text'),
                        visibilityTime: 5000,
                      });
                    },
                  },
                )
              }
              variant="primary"
            />
          </Row>
        </Modal.Card>
      </Modal>

      <ListingAlertModal alert={alert} hideAlert={hideAlert} />
    </>
  );
}
